import './index.scss'

import { useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import VolunteeringController from '@data/controller/VolunteeringController'
import VolunteerTeam, {
  AutoAssignedTraining,
  VolunteerTeamMemeber,
  VolunteerTeamRole
} from '@data/interfaces/Volunteering/volunteerteam.interface'
import useTrainingModules from '@hooks/useTrainingModules'

import closeIcon from 'url:@icon/close.svg';
import bookIcon from 'url:@assets/icons/book.svg';
import pencilIcon from 'url:@assets/icons/pencil.svg';
import trashIcon from 'url:@assets/icons/trash.svg';

// At most one row is being renamed or confirming a delete at a time.
type RowMode = {
  roleID: string;
  kind: 'rename' | 'delete' | 'training';
} | null;

// A rule being edited. The window stays a string until saved, so a half-typed
// number doesn't snap to something else mid-edit.
type TrainingDraftRule = {
  moduleID: string;
  dueInDays: string;
};

// The API's allowed window, and the same default as assigning by hand.
const DUE_IN_DAYS_MIN = 1;
const DUE_IN_DAYS_MAX = 365;
const DEFAULT_DUE_IN_DAYS = 14;

function parseDueInDays(value: string) {
  const days = Number(value);

  return Number.isInteger(days) && days >= DUE_IN_DAYS_MIN && days <= DUE_IN_DAYS_MAX ? days : null;
}

function isSameTraining(draft: TrainingDraftRule[], saved: AutoAssignedTraining[]) {
  return draft.length === saved.length && draft.every((rule, index) => {
    return rule.moduleID === saved[index].moduleID && parseDueInDays(rule.dueInDays) === saved[index].dueInDays;
  });
}

function isSameName(one: string, two: string) {
  return one.trim().toLowerCase() === two.trim().toLowerCase();
}

// Each change saves as it's made, so there's no checkmark: a role exists the
// moment it's added, and deleting one takes it off everyone right away.
export default function ManageRolesModal(props: {
  isOpen: boolean,
  team?: VolunteerTeam | null,
  roles: VolunteerTeamRole[],
  members: VolunteerTeamMemeber[],
  close: () => void,
  onChanged: () => void
}) {
  const auth = useAuth();

  // Seeded once per open. Each response lands here directly, so the list
  // doesn't wait on the page's refetch to catch up.
  const [roles, setRoles] = useState<VolunteerTeamRole[]>(props.roles);
  const [newName, setNewName] = useState('');
  const [rowMode, setRowMode] = useState<RowMode>(null);
  const [renameValue, setRenameValue] = useState('');
  const [trainingDraft, setTrainingDraft] = useState<TrainingDraftRule[]>([]);

  // Only once the modal is open: it stays mounted while hidden.
  const { modules, loading: modulesLoading, error: modulesError } = useTrainingModules({ enabled: props.isOpen });
  const moduleNameByID = new Map(modules.map((module) => [module.id, module.attributes.name]));

  const moduleName = (moduleID: string) => {
    return moduleNameByID.get(moduleID) ?? (modulesLoading ? 'Loading…' : 'Unknown module');
  };

  // What a role hands out, for its row. Counts stand in while names load.
  const trainingSummary = (role: VolunteerTeamRole) => {
    const rules = role.attributes.autoAssignedTraining ?? [];

    if (rules.length === 0) return null;

    if (modulesLoading && modules.length === 0) {
      return `Assigns ${rules.length} training ${rules.length === 1 ? 'module' : 'modules'}`;
    }

    return `Assigns ${rules.map((rule) => `${moduleName(rule.moduleID)} (${rule.dueInDays} ${rule.dueInDays === 1 ? 'day' : 'days'})`).join(', ')}`;
  };
  // 'add' while creating, or the ID of the role being changed.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What the last training save handed out to people already holding the role.
  const [notice, setNotice] = useState<string | null>(null);

  const isBusy = busy !== null;
  const trimmedNewName = newName.trim();
  const sortedRoles = [...roles].sort((one, two) => one.attributes.name.localeCompare(two.attributes.name));

  // The API allows duplicate names, but two "Greeter" roles on one team can't
  // be told apart anywhere they're shown.
  const isNameTaken = (name: string, exceptRoleID?: string) => {
    return roles.some((role) => role.id !== exceptRoleID && isSameName(role.attributes.name, name));
  };

  const isNewNameTaken = trimmedNewName.length > 0 && isNameTaken(trimmedNewName);

  const holderCount = (roleID: string) => {
    return props.members.filter((member) => (member.attributes.roleIDs ?? []).includes(roleID)).length;
  };

  const addRole = async () => {
    if (!auth.churchId || !props.team) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    if (trimmedNewName.length === 0 || isNewNameTaken) return;

    setBusy('add');
    setError(null);

    try {
      const response = await VolunteeringController.createTeamRole(
        auth.churchId,
        props.team.id,
        { name: trimmedNewName, autoAssignedTraining: [] },
        auth
      );
      const created = response.body?.result;

      if (response.error || !created) {
        setError(response.error || 'Unable to create this role right now.');
        return;
      }

      setRoles((currentRoles) => [...currentRoles, created]);
      setNewName('');
      props.onChanged();
    } finally {
      setBusy(null);
    }
  };

  const renameRole = async (role: VolunteerTeamRole) => {
    const name = renameValue.trim();

    if (!auth.churchId || !props.team || name.length === 0) return;

    if (name === role.attributes.name) {
      setRowMode(null);
      return;
    }

    if (isNameTaken(name, role.id)) {
      setError(`This team already has a role named "${name}".`);
      return;
    }

    setBusy(role.id);
    setError(null);

    try {
      // The training rules ride along: the API replaces every attribute, and
      // would read a missing list as none.
      const response = await VolunteeringController.updateTeamRole(
        auth.churchId,
        props.team.id,
        role.id,
        { name, autoAssignedTraining: role.attributes.autoAssignedTraining ?? [] },
        auth
      );

      if (response.error) {
        setError(response.error);
        return;
      }

      const updated = response.body?.result ?? { ...role, attributes: { ...role.attributes, name } };

      setRoles((currentRoles) => currentRoles.map((currentRole) => currentRole.id === role.id ? updated : currentRole));
      setRowMode(null);
      props.onChanged();
    } finally {
      setBusy(null);
    }
  };

  // "Assigned Camera Basics to 3 volunteers." Grouped by module, counting the
  // people each actually went to -- anyone who already had it was skipped.
  const assignedNotice = (assignments: { moduleID: string, personID: string }[]) => {
    if (assignments.length === 0) return null;

    const peopleByModule = new Map<string, Set<string>>();

    for (const assignment of assignments) {
      const people = peopleByModule.get(assignment.moduleID) ?? new Set<string>();
      people.add(assignment.personID);
      peopleByModule.set(assignment.moduleID, people);
    }

    // Array.from rather than a spread: the ES5 target can't spread a Map.
    const parts = Array.from(peopleByModule.entries()).map(([moduleID, people]) => {
      return `${moduleName(moduleID)} to ${people.size} ${people.size === 1 ? 'volunteer' : 'volunteers'}`;
    });

    return `Assigned ${parts.join(', and ')}.`;
  };

  const openTraining = (role: VolunteerTeamRole) => {
    setNotice(null);
    setTrainingDraft((role.attributes.autoAssignedTraining ?? []).map((rule) => ({
      moduleID: rule.moduleID,
      dueInDays: String(rule.dueInDays)
    })));
    setRowMode({ roleID: role.id, kind: 'training' });
    setError(null);
  };

  const saveTraining = async (role: VolunteerTeamRole) => {
    if (!auth.churchId || !props.team) return;

    const rules = trainingDraft.map((rule) => ({ moduleID: rule.moduleID, dueInDays: parseDueInDays(rule.dueInDays) }));

    if (rules.some((rule) => rule.dueInDays === null)) return;

    setBusy(role.id);
    setError(null);
    setNotice(null);

    try {
      const autoAssignedTraining = rules as AutoAssignedTraining[];
      const response = await VolunteeringController.updateTeamRole(
        auth.churchId,
        props.team.id,
        role.id,
        { name: role.attributes.name, autoAssignedTraining },
        auth
      );

      if (response.error) {
        setError(response.error);
        return;
      }

      const updated = response.body?.result ?? { ...role, attributes: { ...role.attributes, autoAssignedTraining } };

      setRoles((currentRoles) => currentRoles.map((currentRole) => currentRole.id === role.id ? updated : currentRole));
      setRowMode(null);
      setNotice(assignedNotice(response.body?.autoAssignedTraining ?? []));
      props.onChanged();
    } finally {
      setBusy(null);
    }
  };

  const deleteRole = async (role: VolunteerTeamRole) => {
    if (!auth.churchId || !props.team) return;

    setBusy(role.id);
    setError(null);

    try {
      const response = await VolunteeringController.deleteTeamRole(auth.churchId, props.team.id, role.id, auth);

      if (response.error) {
        setError(response.error);
        return;
      }

      setRoles((currentRoles) => currentRoles.filter((currentRole) => currentRole.id !== role.id));
      setRowMode(null);
      props.onChanged();
    } finally {
      setBusy(null);
    }
  };

  return <Modal
    id='manage-roles-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='manage-roles-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isBusy}
          onClick={props.close}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>{props.team?.attributes.name} Roles</h3>

        <span className='header-control-spacer' />
      </div>

      <div className='body'>
        <p className='caption'>
          Roles group the people on this team, like "Camera Operator" or "Greeter". A volunteer can hold several.
        </p>

        {error ? <p className='save-error'>{error}</p> : null}
        {notice ? <p className='save-notice' role='status'>{notice}</p> : null}

        <form
          className='add-role'
          onSubmit={(e) => {
            e.preventDefault();
            void addRole();
          }}>
          <input
            type='text'
            value={newName}
            placeholder='New role name'
            aria-label='New role name'
            disabled={isBusy}
            onChange={(e) => setNewName(e.target.value)}
          />

          <button
            type='submit'
            className='add-role-button'
            disabled={isBusy || trimmedNewName.length === 0 || isNewNameTaken}>
            {busy === 'add' ? 'Adding...' : 'Add Role'}
          </button>
        </form>

        {isNewNameTaken ? <p className='field-hint'>This team already has a role with that name.</p> : null}

        {sortedRoles.length === 0 ?
          <p className='empty-roles'>No roles yet. Add one above to start grouping this team.</p> :
          <ul className='role-list'>
            {sortedRoles.map((role) => {
              const mode = rowMode?.roleID === role.id ? rowMode.kind : null;
              const isRowBusy = busy === role.id;
              const holders = holderCount(role.id);
              const holdersLabel = `${holders} ${holders === 1 ? 'volunteer' : 'volunteers'}`;

              if (mode === 'training') {
                const chosen = new Set(trainingDraft.map((rule) => rule.moduleID));
                const available = modules
                  .filter((module) => !chosen.has(module.id))
                  .sort((one, two) => one.attributes.name.localeCompare(two.attributes.name));
                const hasInvalidWindow = trainingDraft.some((rule) => parseDueInDays(rule.dueInDays) === null);
                const isUnchanged = isSameTraining(trainingDraft, role.attributes.autoAssignedTraining ?? []);

                return <li key={role.id} className='role-row is-training'>
                  <p className='role-training-title'>Training for {role.attributes.name}</p>

                  {trainingDraft.length === 0 ?
                    <p className='role-training-empty'>No training yet. People given this role aren't assigned anything.</p> :
                    <ul className='role-training-rules'>
                      {trainingDraft.map((rule, index) => {
                        const name = moduleName(rule.moduleID);

                        return <li key={rule.moduleID} className='role-training-rule'>
                          <span className='role-training-module'>{name}</span>

                          <label className='role-training-due'>
                            Due in
                            <input
                              type='number'
                              inputMode='numeric'
                              min={DUE_IN_DAYS_MIN}
                              max={DUE_IN_DAYS_MAX}
                              step={1}
                              value={rule.dueInDays}
                              aria-label={`Days to complete ${name}`}
                              disabled={isRowBusy}
                              onChange={(e) => {
                                const value = e.target.value;
                                setTrainingDraft((draft) => draft.map((current, currentIndex) => {
                                  return currentIndex === index ? { ...current, dueInDays: value } : current;
                                }));
                              }}
                            />
                            days
                          </label>

                          <button
                            type='button'
                            className='icon-action destructive'
                            aria-label={`Stop assigning ${name}`}
                            disabled={isRowBusy}
                            onClick={() => {
                              setTrainingDraft((draft) => draft.filter((_, currentIndex) => currentIndex !== index));
                            }}>
                            <img src={trashIcon} alt='' aria-hidden='true' />
                          </button>
                        </li>
                      })}
                    </ul>}

                  {hasInvalidWindow ?
                    <p className='field-hint'>Each due window must be a whole number of days, from {DUE_IN_DAYS_MIN} to {DUE_IN_DAYS_MAX}.</p> :
                    null}

                  {modulesError ? <p className='field-hint'>{modulesError}</p> : null}

                  {available.length > 0 ? <select
                    className='role-training-add'
                    value=''
                    aria-label='Add a training module'
                    disabled={isRowBusy}
                    onChange={(e) => {
                      const moduleID = e.target.value;

                      if (!moduleID) return;

                      setTrainingDraft((draft) => [...draft, { moduleID, dueInDays: String(DEFAULT_DUE_IN_DAYS) }]);
                    }}>
                    <option value=''>Add a training module…</option>
                    {available.map((module) => (
                      <option key={module.id} value={module.id}>{module.attributes.name}</option>
                    ))}
                  </select> : null}

                  {!modulesLoading && !modulesError && modules.length === 0 ?
                    <p className='role-training-empty'>This church has no Training Modules yet.</p> :
                    null}

                  <p className='role-training-caption'>
                    {holders > 0 ?
                      `A module you add is also assigned to the ${holdersLabel} who ${holders === 1 ? 'holds' : 'hold'} this role now, due that many days from today, and to anyone given the role later. ` :
                      'Assigned to anyone given this role from now on, due that many days later. '}
                    Changing a due window only affects people given the role later, and removing a module doesn't take
                    it away from anyone who has it.
                  </p>

                  <div className='role-delete-actions'>
                    <button
                      type='button'
                      className='row-action primary'
                      disabled={isRowBusy || hasInvalidWindow || isUnchanged}
                      onClick={() => {
                        void saveTraining(role);
                      }}>
                      {isRowBusy ? 'Saving...' : 'Save'}
                    </button>

                    <button
                      type='button'
                      className='row-action'
                      disabled={isRowBusy}
                      onClick={() => setRowMode(null)}>
                      Cancel
                    </button>
                  </div>
                </li>
              }

              if (mode === 'rename') {
                return <li key={role.id} className='role-row is-editing'>
                  <form
                    className='role-rename'
                    onSubmit={(e) => {
                      e.preventDefault();
                      void renameRole(role);
                    }}>
                    <input
                      type='text'
                      value={renameValue}
                      aria-label={`Rename ${role.attributes.name}`}
                      autoFocus
                      disabled={isRowBusy}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setRowMode(null);
                        }
                      }}
                    />

                    <button
                      type='submit'
                      className='row-action primary'
                      disabled={isRowBusy || renameValue.trim().length === 0}>
                      {isRowBusy ? 'Saving...' : 'Save'}
                    </button>

                    <button
                      type='button'
                      className='row-action'
                      disabled={isRowBusy}
                      onClick={() => setRowMode(null)}>
                      Cancel
                    </button>
                  </form>
                </li>
              }

              if (mode === 'delete') {
                return <li key={role.id} className='role-row is-confirming'>
                  <p className='role-delete-copy'>
                    <strong>Delete {role.attributes.name}?</strong>{' '}
                    {holders > 0 ? `It will be removed from ${holdersLabel}.` : 'No one holds this role.'}
                  </p>

                  <div className='role-delete-actions'>
                    <button
                      type='button'
                      className='row-action destructive'
                      disabled={isRowBusy}
                      onClick={() => {
                        void deleteRole(role);
                      }}>
                      {isRowBusy ? 'Deleting...' : 'Delete'}
                    </button>

                    <button
                      type='button'
                      className='row-action'
                      disabled={isRowBusy}
                      onClick={() => setRowMode(null)}>
                      Cancel
                    </button>
                  </div>
                </li>
              }

              return <li key={role.id} className='role-row'>
                <div className='role-main'>
                  <span className='role-name'>{role.attributes.name}</span>
                  {trainingSummary(role) ? <span className='role-training-summary'>{trainingSummary(role)}</span> : null}
                </div>

                <span className='role-holders'>{holdersLabel}</span>

                <button
                  type='button'
                  className='icon-action'
                  aria-label={`Training for ${role.attributes.name}`}
                  disabled={isBusy}
                  onClick={() => openTraining(role)}>
                  <img src={bookIcon} alt='' aria-hidden='true' />
                </button>

                <button
                  type='button'
                  className='icon-action'
                  aria-label={`Rename ${role.attributes.name}`}
                  disabled={isBusy}
                  onClick={() => {
                    setRenameValue(role.attributes.name);
                    setRowMode({ roleID: role.id, kind: 'rename' });
                    setError(null);
                  }}>
                  <img src={pencilIcon} alt='' aria-hidden='true' />
                </button>

                <button
                  type='button'
                  className='icon-action destructive'
                  aria-label={`Delete ${role.attributes.name}`}
                  disabled={isBusy}
                  onClick={() => {
                    setRowMode({ roleID: role.id, kind: 'delete' });
                    setError(null);
                  }}>
                  <img src={trashIcon} alt='' aria-hidden='true' />
                </button>
              </li>
            })}
          </ul>}
      </div>
    </div>
  </Modal>
}
